/**
 * AssumptionsPanel.jsx v1.0.0 - 2026-10-09
 *
 * Tab 5: shows which inputs the calculation had to assume because the scenario did not provide them
 * (backlog CALC-010). Reads results.assumptions, produced by assumptionLedger.js.
 *
 * - undefined (results saved before the ledger existed): renders nothing
 * - empty list: a one-line confirmation that nothing was assumed
 * - otherwise: a collapsible table of the assumed inputs and where to set them
 *
 * Version history:
 *   v1.0.0 (2026-10-09): initial release
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const unitKey = (unit) => unit.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '');

export default function AssumptionsPanel({ assumptions }) {
  const { t } = useTranslation();

  if (!Array.isArray(assumptions)) return null;

  if (assumptions.length === 0) {
    return (
      <Alert severity="success" sx={{ mb: 2, py: 0.5 }}>
        {t('tab5.assumptions.none')}
      </Alert>
    );
  }

  const unitLabel = (unit) => (unit ? t(`tab5.assumptions.units.${unitKey(unit)}`, { defaultValue: unit }) : '');
  const setInLabel = (setIn) => (
    setIn.length === 0
      ? t('tab5.assumptions.not_settable')
      : setIn.map(n => t('tab5.assumptions.tab_n', { n })).join(' / ')
  );

  return (
    <Accordion disableGutters sx={{ mb: 2, border: '1px solid', borderColor: 'info.light' }} elevation={0}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2" fontWeight={700}>
          {t('tab5.assumptions.title', { count: assumptions.length })}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('tab5.assumptions.intro')}
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{t('tab5.assumptions.col_input')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('tab5.assumptions.col_assumed')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('tab5.assumptions.col_set_in')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assumptions.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{t(`tab5.assumptions.items.${a.id}`, { defaultValue: a.label })}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {String(a.assumed_value)} {unitLabel(a.unit)}
                  </TableCell>
                  <TableCell>{setInLabel(a.set_in)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
}

AssumptionsPanel.propTypes = {
  assumptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    assumed_value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    unit: PropTypes.string,
    set_in: PropTypes.arrayOf(PropTypes.number).isRequired
  }))
};
